import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import Dialog from '../common/dialog'
import { Pocket } from '../../utils/dexie/db'
import { numberWithCommas } from '../../utils'
import { countriesCurrencies } from '../../lib'
import { requestAccessToken } from '../../api/auth.api'
import { usePockets } from '../pockets-management/hooks/use-pockets'
import { handleDecryptPocketData } from '../pockets-management/utils'
import { useSecureStorage } from '../../contexts/secure-storage.context'
import { AppStateContext, AuthContext, UserContext } from '../../contexts'
import { CustomerCompanyManageDataModal } from './customer-company-manage-data-modal'
import { UserSessionType } from '../../types'

export const CustomerCompanyBalanceData = ({
  selectedCountry,
  customerCompanyProfile,
  handleOpenCustomerCompanyFundingDataModal
}: {
  selectedCountry?: string
  handleOpenCustomerCompanyFundingDataModal: () => void
  customerCompanyProfile?: { balance: string; businessName: string; businessDocumentNumber: string; businessEmail: string }
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { customerCompanyId } = useParams()
  const { secret, handleGetSecret } = useSecureStorage()
  const { pockets } = usePockets(String(secret))
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { currentPocketsData, handleGetCurrentPocketsData } = useContext(AppStateContext)
  const { t } = useTranslation(['customer-company-details-management', 'customer-companies-management'])

  const [balanceValueToDisplay, setBalanceValueToDisplay] = useState<number>(0)
  // const [currentPocketsData, setCurrentPocketsData] = useState<Array<Pocket>>([])
  const [isLoadingWalletsBalances, setIsLoadingWalletsBalances] = useState<boolean>(false)
  const [openWalletsBalancesModal, setOpenWalletsBalancesModal] = useState<boolean>(false)
  const [openCustomerCompanyManageDataModal, setOpenCustomerCompanyManageDataModal] = useState<boolean>(false)

  useEffect(() => {
    if (userSession && location && pockets && location.pathname.indexOf('insights') > -1 && userSession.role.indexOf('super-admin') === -1) {
      handleGetPocketData(pockets)
      return
    }

    if (customerCompanyProfile && userSession && userSession.role.indexOf('super-admin') > -1) {
      setBalanceValueToDisplay(Number(customerCompanyProfile.balance))
      handleGetPocketsBalances()
    }

    setBalanceValueToDisplay(Number(customerCompanyProfile?.balance))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCompanyProfile, userSession, pockets])

  const handleGetPocketData = async (pockets: Array<Pocket>) => {
    const secretGenerated = await handleGetSecret()
    const pocketDataByAdminUser = pockets.find((pocketData) => String(pocketData.id) === userSession?.adminOfPockets?.[0]) as Pocket

    const pocketDataDecrypted = await handleDecryptPocketData(pocketDataByAdminUser, String(secretGenerated))
    setBalanceValueToDisplay(Number(pocketDataDecrypted.balance))
  }

  const handleDisplayPocketBalances = () => {
    if (Array.isArray(currentPocketsData) && currentPocketsData.length > 0) {
      setIsLoadingWalletsBalances(false)
    }
  }

  const handleGetPocketsBalances = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      handleGetCurrentPocketsData?.(String(customerCompanyId), userSession as UserSessionType, accessToken)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <>
      <div className="customer-company-details-profile-balance-brief-data">
        <div
          style={!userSession?.subRole || (userSession?.subRole && userSession?.subRole !== 'compliance-specialist') ? { cursor: 'pointer' } : {}}
          onClick={() => {
            if (!userSession?.subRole || (userSession?.subRole && userSession?.subRole !== 'compliance-specialist')) {
              setIsLoadingWalletsBalances(true)
              setOpenWalletsBalancesModal(true)
              handleDisplayPocketBalances()
            }
          }}
        >
          <div className="subtitle">{t('currentBalance')}</div>
          <div className="balance-data">{`$ ${numberWithCommas(Number(balanceValueToDisplay).toFixed(2))} ${selectedCountry ? (countriesCurrencies as { [key: string]: string })[selectedCountry] : 'COP'}`}</div>
        </div>
        {userSession?.subRole && (userSession?.subRole === 'payments-specialist' || userSession?.subRole === 'crypto-specialist') && (
          <div
            onClick={(event) => {
              event.stopPropagation()
              handleOpenCustomerCompanyFundingDataModal()
            }}
            className="users-management-user-data-status process-funding-button-container"
          >
            <div className="process-funding-button">{t('processFunding', { ns: 'customer-companies-management' })}</div>
          </div>
        )}
        {userSession?.subRole && userSession?.subRole === 'compliance-specialist' && (
          <div
            onClick={(event) => {
              event.stopPropagation()
              // setIsLoadingWalletsBalances(true)
              // setOpenWalletsBalancesModal(true)
              // handleGetPocketsBalances()
              setOpenCustomerCompanyManageDataModal(true)
            }}
            className="users-management-user-data-status process-funding-button-container"
          >
            <div className="process-funding-button">{t('viewWallets', { ns: 'customer-companies-management' })}</div>
          </div>
        )}
      </div>
      {openCustomerCompanyManageDataModal && (
        <CustomerCompanyManageDataModal
          customerCompanyId={customerCompanyId}
          customerCompanyDataToUpdate="pockets"
          handleCloseSession={handleCloseSession}
          handleCloseModal={(openStateToSet: boolean) => setOpenCustomerCompanyManageDataModal(openStateToSet)}
        />
      )}
      {openWalletsBalancesModal && (
        <Dialog
          hideActionsButtons
          dialogTitle="Saldos por bolsillos"
          dialogIsLoading={isLoadingWalletsBalances}
          handleDialogOpenState={(openStateToSet: boolean) => setOpenWalletsBalancesModal(openStateToSet)}
        >
          <div style={{ width: '100%' }}>
            {currentPocketsData?.map((pocketData: Pocket) => {
              return (
                <div
                  key={pocketData._id}
                  style={{ padding: '10px 20px', boxSizing: 'border-box', textAlign: 'center', borderBottom: '1px solid #E5E5E5' }}
                >{`${pocketData.name?.toUpperCase()} = $ ${numberWithCommas(Math.round(Number(pocketData.balance)))} ${(countriesCurrencies as { [key: string]: string })[String(selectedCountry)].toUpperCase()}`}</div>
              )
            })}
          </div>
        </Dialog>
      )}
    </>
  )
}
